import { useEffect, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loadingLabel?: string;
  variant?: 'danger' | 'warning';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const variantStyles = {
  danger: {
    button: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    button: 'bg-amber-600 hover:bg-amber-700',
  },
};

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Eliminar',
  cancelLabel = 'Cancelar',
  loadingLabel = 'Procesando...',
  variant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmModalProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) onCancel();
  }, [loading, onCancel]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const styles = variantStyles[variant];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={loading ? undefined : onCancel}
    >
      <div
        className="w-full max-w-[420px] rounded-xl bg-white p-6 text-center shadow-xl animate-[slideUp_200ms_ease-out] sm:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <AlertTriangle className="h-7 w-7" />
        </div>

        <h2 className="mb-3 text-xl font-semibold text-gray-800">{title}</h2>
        <p className="mb-6 text-[15px] text-gray-500">{message}</p>

        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <button
            type="button"
            className={`inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg text-[15px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${styles.button}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="h-[18px] w-[18px] animate-spin" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                  <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {loadingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </button>
          <button
            type="button"
            className="inline-flex h-11 flex-1 items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-[15px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-70"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
